import React from 'react';
import moment from 'moment';
import { availableAreaColors } from 'src/shared/constants/colors';
import QuestionButtonHelp from 'src/shared/components/question-button-help/QuestionButtonHelp';
import useProject from 'src/shared/hooks/useProject';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import usePermissions from 'src/shared/hooks/usePermissions';
import { Access, ProjectSamplingCadenceType, Resources } from 'src/shared/types';
import { SecondaryButton } from 'src/shared/components/button/Button';
import { STANDARD_DATE_FORMAT } from 'src/shared/constants/dateConstants';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import { useSurveyDesignQuery } from 'src/app/surveys/state/api/surveyGraphSlice';
import { User as PersonOutlineOutlinedIcon } from 'lucide-react';
import styles from '../new-survey-design/components/review-and-submit/ReviewAndSubmit.module.scss';
import ReviewSamples from '../new-survey-design/components/review-and-submit/components/ReviewSamples';

const SurveyDesignSummary = () => {
    const { currentProjectId } = useProject();
    const { hasPermission } = usePermissions();
    const navigate = useAppNavigation();

    const { currentData: surveyDesign } = useSurveyDesignQuery(
        {
            projectId: currentProjectId || '',
        },
        {
            skip: !currentProjectId,
        }
    );

    const survey = surveyDesign;
    if (!survey) {
        return null;
    }
    const {
        customerId,
        customerName,
        projectName,
        projectCode,
        subscription,
        country,
        leadRole,
        surveyPublishDate,
        samplingCadence,
        surveySamples,
        sampleGroups,
        habitatAssayTypes,
        leadEmail,
        leadName,
    } = survey;

    const canAdminSurveyDesign = hasPermission({
        to: [Access.CREATE, Access.UPDATE],
        customerId: customerId,
        resource: Resources.SURVEY_DESIGN,
    });

    const mainContent = (
        <>
            <div className={styles.reviewAndSubmit}>
                <div className={styles.section}>
                    <div className={styles.sectionTitle}>
                        <span>Survey details</span>
                    </div>
                    <div className={styles.sectionItems}>
                        <div className={styles.sectionItem}>
                            <div className={styles.label}>Customer name</div>
                            <div className={styles.value}>{customerName}</div>
                        </div>

                        <div className={styles.sectionItem}>
                            <div className={styles.label}>Survey design name</div>
                            <div className={styles.value}>{projectName}</div>
                        </div>

                        <div className={styles.sectionItem}>
                            <div className={styles.label}>Project code</div>
                            <div className={styles.value}>{projectCode}</div>
                        </div>

                        <div className={styles.sectionItem}>
                            <div className={styles.label}>Subscription type</div>
                            <div className={styles.value}>{subscription?.label}</div>
                        </div>

                        <div className={styles.sectionItem}>
                            <div className={styles.label}>Survey publish date</div>
                            <div className={styles.value}>{moment(surveyPublishDate).format(STANDARD_DATE_FORMAT)}</div>
                        </div>

                        <div className={styles.sectionItem}>
                            <div className={styles.label}>Sampling country</div>
                            <div className={styles.value}>{country}</div>
                        </div>
                    </div>
                    <div className={styles.sectionItems}>
                        <div className={styles.sectionItem}>
                            <div className={styles.label}>Customer contact</div>
                            <div className={styles.customerContact}>
                                <div className={styles.userIcon}>
                                    <PersonOutlineOutlinedIcon />
                                </div>
                                <div className={styles.contactDetails}>
                                    <div className={styles.contactDetailsName}>{leadName}</div>
                                    <div>{leadEmail}</div>
                                </div>
                                <div className={styles.role}>
                                    <div>{leadRole}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.section}>
                    <div className={styles.sectionTitle}>
                        <span>Survey aims</span>
                    </div>
                    <div className={styles.sectionItems}>
                        <div className={styles.sectionItem}>
                            <div className={styles.label}>
                                Sample groups <QuestionButtonHelp type='api' slug='project-definition-sample-groups' />
                            </div>
                            <div className={styles.sampleGroups}>
                                {sampleGroups.map((entry, index) => {
                                    return (
                                        <div key={index} className={styles.sampleGroupTag}>
                                            <div
                                                className={styles.sampleGroupTagColor}
                                                style={{ backgroundColor: availableAreaColors[index] }}
                                            ></div>
                                            <div className={styles.sampleGroupTagValue}>{entry}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className={styles.sectionItem}>
                            <div className={styles.label}>
                                Data sources <QuestionButtonHelp type='api' slug='project-definition-habitat-test-type' />
                            </div>
                            <div className={styles.habitatAssayTypes}>
                                {habitatAssayTypes.map((entry, index) => {
                                    return (
                                        <div key={index} className={styles.habitatAssayTypeTag}>
                                            <div key={index} className={styles.habitatAssayTypeTagValue}>
                                                <div className={styles.habitatName}>{entry.habitatName}</div>
                                                <div className={styles.assayName}>{entry.assayName}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.section}>
                    <div className={styles.sectionTitle}>
                        <span>Sampling cadence</span>
                    </div>
                    <div className={styles.sectionItems}>
                        <div className={styles.sectionItem}>
                            <div className={styles.label}>Multi year or single year sampling</div>
                            <div className={styles.value}>
                                {samplingCadence?.type === ProjectSamplingCadenceType.SINGLE_YEAR ? 'Single year' : 'Multi year'}
                            </div>
                        </div>

                        <div className={styles.sectionItem}>
                            <div className={styles.label}>
                                Sampling events per year <QuestionButtonHelp type='api' slug='sampling-events-per-year' />
                            </div>
                            <div className={styles.value}>{samplingCadence?.samplingEventsPerYear}</div>
                        </div>
                    </div>
                    <div className={styles.sectionItems}>
                        <div className={styles.samplingEvents}>
                            {samplingCadence?.events.map((entry, index) => {
                                return (
                                    <div key={index} className={styles.samplingEvent}>
                                        <div className={styles.eventNumber}>
                                            Sampling event {index + 1}{' '}
                                            <QuestionButtonHelp type='api' slug='project-definition-sampling-event' />
                                        </div>
                                        <div className={styles.eventName}>{entry.name}</div>
                                        <div className={styles.datesTitle}>Expected dates</div>
                                        <div className={styles.dates}>
                                            <span className={styles.dateLabel}>From</span>
                                            <span className={styles.dateValue}>{moment(entry.fromDate).format(STANDARD_DATE_FORMAT)}</span>
                                            <span className={styles.dateLabel}>To</span>
                                            <span className={styles.dateValue}>{moment(entry.toDate).format(STANDARD_DATE_FORMAT)}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className={styles.section}>
                    <div className={styles.sectionTitle}>
                        <span>Samples</span>
                    </div>
                    <ReviewSamples sampleGroups={sampleGroups} surveySamples={surveySamples} />
                </div>
            </div>
        </>
    );

    const otherActions = (
        <div className={styles.otherActions}>
            {canAdminSurveyDesign && <SecondaryButton onClick={navigate.toSurveyAdmin}>Survey admin</SecondaryButton>}
        </div>
    );

    return <StandardAppLayout title='Survey design summary' subTitle={projectName} mainContent={mainContent} otherActions={otherActions} />;
};

export default SurveyDesignSummary;
